import { useMemo, type ReactNode } from 'react';
import { Avatar, Button, Dropdown, Space, type MenuProps } from 'antd';
import {
  DashboardOutlined,
  EditOutlined,
  EyeOutlined,
  LogoutOutlined,
  SettingOutlined,
  ShopOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useAppDispatch } from '@store/store.tsx';
import { logout } from '@store/slices/authSlice.tsx';
import type { UserProfile } from '@app-types/user.types.ts';
import type { CmsAccessState } from '@features/cms/hooks/useCmsAccess.ts';
import { getLegacyEditMenuLabel } from './editModeLabel';
import { usePublicSitePreview } from '../../../contexts/PublicSitePreviewContext.tsx';

export { getLegacyEditMenuLabel } from './editModeLabel';

export interface StaffQuickLink {
  key: string;
  label: string;
  to: string;
  icon?: ReactNode;
}

interface StaffPublicTopbarProps {
  cmsAccess: CmsAccessState;
  profile: UserProfile | null | undefined;
  quickLinks: StaffQuickLink[];
  onEnterEditMode: () => void;
  onExitEditMode?: () => void;
  editMode?: boolean;
  themeToggle?: ReactNode;
  variant?: 'fixed' | 'inline';
}

const iconByKey: Record<string, ReactNode> = {
  dashboard: <DashboardOutlined />,
  eshop: <ShopOutlined />,
  erp: <ToolOutlined />,
};

const StaffPublicTopbar = ({
  cmsAccess,
  profile,
  quickLinks,
  onEnterEditMode,
  onExitEditMode,
  editMode = false,
  themeToggle,
  variant = 'fixed',
}: StaffPublicTopbarProps) => {
  const dispatch = useAppDispatch();
  const displayName = profile?.first_name || profile?.username || 'Staff';
  const email = profile?.email || profile?.username || displayName;
  const avatarLetter = displayName.charAt(0).toUpperCase();
  const canEnterEditMode = cmsAccess.status === 'allowed';
  const { previewActive, exitPreview } = usePublicSitePreview();

  const menuItems = useMemo<NonNullable<MenuProps['items']>>(() => {
    const linkItems = quickLinks.map(({ key, label, to, icon }) => ({
      key: `link:${to}`,
      label: key === 'dashboard' ? 'Panel' : label,
      icon: icon ?? iconByKey[key] ?? <DashboardOutlined />,
    }));

    return [
      {
        key: 'user',
        label: email,
        disabled: true,
        icon: <UserOutlined />,
      },
      { type: 'divider' },
      ...linkItems,
      ...(canEnterEditMode
        ? [{
          key: 'edit',
          label: getLegacyEditMenuLabel(editMode),
          icon: <EditOutlined />,
        }]
        : []),
      ...(profile
        ? [{
          key: 'account',
          label: 'Mi cuenta',
          icon: <SettingOutlined />,
        }]
        : []),
      ...(previewActive
        ? [{
          key: 'public-preview:close',
          label: 'Cerrar Preview',
          icon: <EyeOutlined />,
        }]
        : []),
      { type: 'divider' },
      {
        key: 'logout',
        label: 'Salir',
        icon: <LogoutOutlined />,
      },
    ];
  }, [canEnterEditMode, editMode, email, previewActive, profile, quickLinks]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const itemKey = String(key);
    if (itemKey === 'edit') {
      if (editMode) {
        onExitEditMode?.();
      } else {
        onEnterEditMode();
      }
      return;
    }
    if (itemKey === 'public-preview:close') {
      exitPreview();
      return;
    }
    if (itemKey.startsWith('link:')) {
      window.location.assign(itemKey.slice(5));
      return;
    }
    if (itemKey === 'account') {
      window.location.assign('/eshop/account');
      return;
    }
    if (itemKey === 'logout') {
      dispatch(logout());
    }
  };

  const content = (
      <Space align="center" size={8}>
        {themeToggle}
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          trigger={['click']}
          placement="bottomRight"
          overlayClassName="cms-staff-topbar__menu"
          getPopupContainer={variant === 'inline' ? (trigger) => trigger.parentElement ?? document.body : undefined}
        >
          <Button
            type="text"
            className={`cms-staff-topbar__user ${variant === 'inline' ? 'cms-staff-topbar__user--inline' : ''}`}
            aria-label="Abrir menu de usuario"
            icon={
              profile?.avatar_url ? (
                <Avatar size="small" src={profile.avatar_url} alt={displayName} />
              ) : (
                <Avatar size="small" alt={displayName}>
                  {avatarLetter}
                </Avatar>
              )
            }
          >
            {variant === 'inline' && <span className="cms-staff-topbar__user-label">Cuenta</span>}
          </Button>
        </Dropdown>
      </Space>
  );

  return variant === 'inline'
    ? <div className="cms-staff-topbar cms-staff-topbar--inline" role="navigation" aria-label="Menu de usuario">{content}</div>
    : <div className="cms-staff-topbar" role="navigation" aria-label="Menu de staff">{content}</div>;
};

export default StaffPublicTopbar;
